import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import FieldHint from "../components/FieldHint";
import { api, ApiError, type RankingSystemSummary } from "../lib/api";
import { describeRules } from "../lib/customRanking";

/** Sección Ranking del admin (pedido de Eduardo 2026-07-15): sistemas
 * oficiales + importación de listados FMT (se congelan como publicación:
 * alimentan siembra y ranking público) y WTN (id + ratings en el
 * perfil del jugador). Es el puente hasta la conexión oficial ITF/TODS. */

type CsvRow = Record<string, string>;
type SystemsLoadState = "loading" | "loaded" | "failed";

export function parseCsv(text: string): CsvRow[] {
  const lines = text.replace(/^﻿/, "").split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const split = (line: string): string[] => {
    const cells: string[] = [];
    let current = "";
    let quoted = false;
    for (let index = 0; index < line.length; index += 1) {
      const char = line[index];
      if (char === '"') {
        if (quoted && line[index + 1] === '"') { current += '"'; index += 1; } else { quoted = !quoted; }
      } else if (char === "," && !quoted) {
        cells.push(current.trim()); current = "";
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    return cells;
  };
  const headers = split(lines[0]).map((cell) => cell.toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = split(line);
    const row: CsvRow = {};
    headers.forEach((header, index) => { row[header] = cells[index] ?? ""; });
    return row;
  });
}

const FMT_LAYOUT = "posicion,nombre,id_federativo,email";
const WTN_LAYOUT = "nombre,id_federativo,email,wtn_id,wtn_singles,wtn_dobles";

function downloadLayout(filename: string, header: string, example: string) {
  // BOM para que Excel lo abra con acentos correctos; se edita ahí y se
  // guarda como CSV para volver a subirlo.
  const blob = new Blob([`﻿${header}
${example}
`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function AdminRankingHubPage() {
  const { token, user } = useAuth();
  const [systems, setSystems] = useState<RankingSystemSummary[]>([]);
  const [systemsLoadState, setSystemsLoadState] = useState<SystemsLoadState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [fmtSystemId, setFmtSystemId] = useState<string>("");
  const [fmtCategoryId, setFmtCategoryId] = useState<string>("");
  const [fmtCsv, setFmtCsv] = useState("");
  const [wtnCsv, setWtnCsv] = useState("");
  const [unmatched, setUnmatched] = useState<string[]>([]);

  const isSuperadmin = Boolean(user?.roles?.includes("superadmin"));
  const fmtFileRef = useRef<HTMLInputElement | null>(null);
  const wtnFileRef = useRef<HTMLInputElement | null>(null);

  async function readCsvFile(event: React.ChangeEvent<HTMLInputElement>, apply: (text: string) => void) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (/\.xlsx?$/i.test(file.name)) {
      setError("Los archivos de Excel deben guardarse como CSV primero (Archivo → Guardar como → CSV). El layout descargable ya abre en Excel.");
      return;
    }
    try {
      apply(await file.text());
      setError(null);
      setMessage(`Archivo "${file.name}" cargado. Revisa el contenido y presiona importar.`);
    } catch {
      setError(`No pudimos leer el archivo "${file.name}".`);
    }
  }

  const load = useCallback(async () => {
    if (!token) return;
    setSystemsLoadState("loading");
    setError(null);
    try {
      const response = await api.rankings.listSystems(token);
      setSystems(response.data.systems);
      setSystemsLoadState("loaded");
    } catch (err) {
      setSystemsLoadState("failed");
      setError(err instanceof ApiError ? err.message : "Error de red.");
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const fmtSystem = useMemo(
    () => systems.find((system) => String(system.id) === fmtSystemId) ?? null,
    [systems, fmtSystemId],
  );

  async function handleImportFmt() {
    if (!token || !fmtSystem || !fmtCategoryId) return;
    const rows = parseCsv(fmtCsv).map((row) => ({
      position: row.posicion ? Number(row.posicion) : null,
      name: row.nombre || null,
      federation_id: row.id_federativo || null,
      email: row.email || null,
    }));
    if (rows.length === 0) {
      setError(`El CSV está vacío o sin encabezados. Formato esperado: ${FMT_LAYOUT}`);
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    setUnmatched([]);
    try {
      const response = await api.rankings.importList(token, fmtSystem.id, {
        age_category_id: Number(fmtCategoryId),
        rows,
      });
      setMessage(
        `Listado importado como publicación #${response.data.publication_id}: ${response.data.matched} jugador(es) cruzados. ` +
        "Ya está disponible para aplicar la siembra desde la tarjeta del ranking oficial y para el ranking público.",
      );
      setUnmatched(response.data.unmatched);
      setFmtCsv("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handleImportWtn() {
    if (!token) return;
    const rows = parseCsv(wtnCsv).map((row) => ({
      name: row.nombre || null,
      federation_id: row.id_federativo || null,
      email: row.email || null,
      wtn_id: row.wtn_id || null,
      rating: row.wtn_singles || null,
      rating_doubles: row.wtn_dobles || null,
    }));
    if (rows.length === 0) {
      setError(`El CSV está vacío o sin encabezados. Formato esperado: ${WTN_LAYOUT}`);
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    setUnmatched([]);
    try {
      const response = await api.rankings.importWtn(token, { rows });
      setMessage(`WTN actualizado en ${response.data.updated} perfil(es).`);
      setUnmatched(response.data.unmatched);
      setWtnCsv("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  const customSystems = systems.filter((system) => system.is_custom);
  const officialSystems = systems.filter((system) => !system.is_custom);

  // «Reactivar» un ranking propio pausado desde su tarjeta (misma acción
  // que «Reactivar ranking» dentro de él).
  async function handleReactivate(system: RankingSystemSummary) {
    if (!token || busy) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.rankings.updateCustomSystem(token, system.id, { is_active: true });
      setMessage(`${system.name}: ranking activo otra vez.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  function goToFmtImport() {
    const panel = document.getElementById("ranking-import-fmt");
    panel?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    panel?.querySelector<HTMLElement>("select")?.focus({ preventScroll: true });
  }

  function goToWtnImport() {
    const panel = document.getElementById("ranking-import-wtn") ?? document.getElementById("ranking-wtn-status");
    panel?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="adm-rank">
      <header className="adm-players__head">
        <div>
          <span className="adm-players__eyebrow">Puntos y listados</span>
          <h1 className="adm-players__title">
            <span className="hint-label">
              Ranking
              <FieldHint label="Qué hay en Ranking">
                Sistemas oficiales de puntos, listados FMT y datos WTN. Lo que importes aquí sirve para la siembra oficial (tarjeta del ranking oficial en el paso 7) y para el ranking público. El botón «Usar rank FMT» del paso 7 sigue usando los standings actuales, no estas listas.
              </FieldHint>
            </span>
          </h1>
        </div>
        <div className="adm-players__head-actions">
          {isSuperadmin ? (
            <Link className="adm-players__btn adm-players__btn--secondary" to="/superadmin/rankings">
              Administrar sistemas
            </Link>
          ) : null}
          <Link className="adm-players__btn adm-players__btn--primary" to="/admin/rankings/propio/nuevo">+ Crear mi ranking</Link>
        </div>
      </header>
      {error ? <p role="alert" className="form-error">{error}</p> : null}
      {message ? <p className="form-message">{message}</p> : null}
      {unmatched.length > 0 ? (
        <div className="alert alert--info">
          <strong>{unmatched.length} renglón(es) sin cruce</strong> (revísalos manualmente):{" "}
          {unmatched.slice(0, 15).join(", ")}{unmatched.length > 15 ? "…" : ""}
        </div>
      ) : null}

      {/* Eduardo 2026-09-22: el organizador administra su PROPIO ranking. */}
      <h2 className="adm-rank__section">
        <span className="hint-label">
          Mis rankings
          <FieldHint label="Qué es un ranking propio">
            Tu propio sistema de puntos: categorías libres, puntos por partido ganado/perdido y bonos por posición final. Ligas tus torneos desde el paso 7 del wizard y cada resultado suma o resta puntos solo. Puedes subir tu lista actual como puntos iniciales y publicar el ranking cuando quieras.
          </FieldHint>
        </span>
      </h2>
      <div className="adm-rank__grid">
        {customSystems.map((system) => {
          const featured = system.id === (customSystems.find((item) => item.is_active) ?? customSystems[0])?.id;
          const categoriesCount = system.categories.length;
          const meta = [
            `${categoriesCount} ${categoriesCount === 1 ? "categoría" : "categorías"}`,
            describeRules(system.rules),
          ].filter(Boolean).join(" · ");
          return (
            <article key={system.id} className={`adm-rank__card${featured ? " adm-rank__card--main" : ""}`}>
              <div className="adm-rank__card-top">
                <strong className="adm-rank__name">{system.name}</strong>
                <span className={`adm-rank__chip${system.is_active ? " adm-rank__chip--on" : ""}`}>{system.is_active ? "Activo" : "Pausado"}</span>
              </div>
              <span className="adm-rank__meta">{meta}</span>
              {categoriesCount > 0 ? (
                <span className="adm-rank__cats">{system.categories.map((category) => category.name).join(" · ")}</span>
              ) : null}
              {system.players_count != null ? (
                <span className="adm-rank__stats">
                  {system.players_count} {system.players_count === 1 ? "jugador" : "jugadores"} · {system.tournaments_count ?? 0}{" "}
                  {(system.tournaments_count ?? 0) === 1 ? "torneo" : "torneos"}
                </span>
              ) : null}
              {system.top3 && system.top3.length > 0 ? (
                <ol className="adm-rank__top3" aria-label={`Primeros lugares de ${system.name}`}>
                  {system.top3.map((row) => (
                    <li key={row.player_profile_id}>
                      <span className="adm-rank__top3-pos">{row.position}</span>
                      <span className="adm-rank__top3-name">{row.display_name}</span>
                      <span className="adm-rank__top3-pts">{row.points_total} pts</span>
                    </li>
                  ))}
                </ol>
              ) : null}
              <div className="adm-rank__actions">
                <Link className="adm-rank__btn adm-rank__btn--primary" to={`/admin/rankings/propio/${system.id}`}>Administrar</Link>
                {system.is_active ? (
                  <Link className="adm-rank__btn" to={`/rankings/${system.slug}`}>Ver ranking público</Link>
                ) : (
                  <button className="adm-rank__btn" type="button" onClick={() => void handleReactivate(system)} disabled={busy}>
                    Reactivar
                  </button>
                )}
              </div>
            </article>
          );
        })}
        <Link className="adm-rank__new" to="/admin/rankings/propio/nuevo">
          <span className="adm-rank__new-plus" aria-hidden="true">+</span>
          <strong>Crear mi ranking</strong>
          <span>
            {systemsLoadState === "loaded" && customSystems.length === 0
              ? "Todavía no tienes un ranking propio. Créalo en un minuto y liga tus torneos desde su paso de Ranking."
              : "Partido ganado + campeón y finalista. Listo en un minuto."}
          </span>
        </Link>
      </div>

      <h2 className="adm-rank__section">Sistemas oficiales</h2>
      {systemsLoadState === "loading" ? (
        <p role="status" className="muted">Cargando sistemas…</p>
      ) : systemsLoadState === "failed" ? (
        <div>
          <p className="muted">No pudimos cargar los sistemas de ranking.</p>
          <button className="btn btn--ghost" type="button" onClick={() => void load()}>
            Reintentar
          </button>
        </div>
      ) : (
        <div className="adm-rank__grid adm-rank__grid--official">
          {officialSystems.length === 0 ? (
            <p className="muted">
              No hay sistemas oficiales aún. {isSuperadmin ? "Crea el ranking FMT desde «Administrar sistemas»." : "Pide al superadmin activar el sistema oficial."}
            </p>
          ) : (
            officialSystems.map((system) => (
              <article key={system.id} className="adm-rank__card adm-rank__card--official">
                <div className="adm-rank__card-top">
                  <strong className="adm-rank__name">{system.name}</strong>
                  <span className={`adm-rank__chip${system.is_active ? " adm-rank__chip--ok" : ""}`}>{system.is_active ? "Vigente" : "Pausado"}</span>
                </div>
                <span className="adm-rank__meta">
                  Puntos oficiales de la federación{system.categories.length > 0 ? ` · ${system.categories.map((category) => category.name).join(" · ")}` : ""}.
                </span>
                <div className="adm-rank__actions">
                  <button className="adm-rank__btn adm-rank__btn--primary" type="button" onClick={goToFmtImport}>Importar listado</button>
                  <Link className="adm-rank__btn" to={`/rankings/${system.slug}`}>
                    Ver ranking público
                  </Link>
                </div>
              </article>
            ))
          )}
          <article className="adm-rank__card adm-rank__card--official">
            <div className="adm-rank__card-top">
              <strong className="adm-rank__name">WTN</strong>
              <span className="adm-rank__chip">Por listado</span>
            </div>
            <span className="adm-rank__meta">ID y ratings WTN en el perfil del jugador.</span>
            <div className="adm-rank__actions">
              <button className="adm-rank__btn adm-rank__btn--primary" type="button" onClick={goToWtnImport}>
                {isSuperadmin ? "Importar WTN" : "Ver estado"}
              </button>
            </div>
          </article>
        </div>
      )}

      <section className="adm-players__panel adm-rank__panel" id="ranking-import-fmt">
        <h2 className="adm-rank__h2">
          <span className="hint-label">
            Importar listado FMT
            <FieldHint label="¿Cómo funciona? Importación FMT">
              Pega o carga el CSV del ranking oficial vigente ({FMT_LAYOUT}). Se guarda como publicación congelada del sistema: la usa «Aplicar siembra» en la tarjeta del ranking oficial (paso 6) y el ranking público. El cruce con tus jugadores es por ID federativo, correo o nombre normalizado; los renglones sin cruce quedan guardados y se conectan solos cuando el jugador se registre. No cambia el origen del botón «Usar rank FMT».
            </FieldHint>
          </span>
        </h2>
        <p className="adm-rank__sub">Pega o carga el CSV del ranking oficial vigente.</p>
        <div className="form">
          <div className="official-ranking-card__form">
            <label>
              Sistema
              <select className="t123-select" value={fmtSystemId} onChange={(e) => { setFmtSystemId(e.target.value); setFmtCategoryId(""); }} disabled={busy}>
                <option value="">Seleccionar sistema</option>
                {systems.filter((system) => !system.is_custom).map((system) => (
                  <option key={system.id} value={system.id}>{system.name}</option>
                ))}
              </select>
            </label>
            <label>
              Categoría de edad
              <select className="t123-select" value={fmtCategoryId} onChange={(e) => setFmtCategoryId(e.target.value)} disabled={busy || !fmtSystem}>
                <option value="">Seleccionar</option>
                {(fmtSystem?.categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="form__actions">
            <button className="btn btn--ghost" type="button" onClick={() => downloadLayout("layout-listado-fmt.csv", FMT_LAYOUT, "1,Ana María Torres,FMT-001,ana@correo.mx")} disabled={busy}>
              Descargar layout
            </button>
            <button className="btn btn--ghost" type="button" onClick={() => fmtFileRef.current?.click()} disabled={busy}>
              Cargar layout
            </button>
            <input ref={fmtFileRef} type="file" accept=".csv,.txt,text/csv" hidden onChange={(e) => void readCsvFile(e, setFmtCsv)} />
          </div>
          <label>
            CSV del listado (o cárgalo como archivo)
            <textarea
              rows={7}
              value={fmtCsv}
              onChange={(e) => setFmtCsv(e.target.value)}
              placeholder={`${FMT_LAYOUT}\n1,Ana María Torres,FMT-001,ana@correo.mx\n2,Beto Juárez,,beto@correo.mx`}
              disabled={busy}
            />
          </label>
          <div className="form__actions">
            <button className="adm-players__btn adm-players__btn--primary" type="button" onClick={() => void handleImportFmt()} disabled={busy || !fmtSystem || !fmtCategoryId}>
              {busy ? "Importando…" : "Importar como publicación"}
            </button>
          </div>
        </div>
      </section>

      {isSuperadmin ? (
        <section className="adm-players__panel adm-rank__panel" id="ranking-import-wtn">
          <h2 className="adm-rank__h2">
            <span className="hint-label">
              Importar WTN
              <FieldHint label="¿Cómo funciona? Importación WTN">
                Actualiza el ID y los ratings WTN ({WTN_LAYOUT}) en el perfil global del jugador. Escala oficial 1–40; valores fuera de escala se ignoran. Los renglones sin cruce quedan guardados y se aplican solos cuando el jugador se registre con su WTN o ID federativo.
              </FieldHint>
            </span>
          </h2>
          <p className="adm-rank__sub">ID y ratings WTN en el perfil del jugador.</p>
          <div className="form">
            <div className="form__actions">
              <button className="btn btn--ghost" type="button" onClick={() => downloadLayout("layout-wtn.csv", WTN_LAYOUT, "Ana María Torres,FMT-001,ana@correo.mx,WTN-1234,23.4,25.1")} disabled={busy}>
                Descargar layout
              </button>
              <button className="btn btn--ghost" type="button" onClick={() => wtnFileRef.current?.click()} disabled={busy}>
                Cargar layout
              </button>
              <input ref={wtnFileRef} type="file" accept=".csv,.txt,text/csv" hidden onChange={(e) => void readCsvFile(e, setWtnCsv)} />
            </div>
            <label>
              CSV del listado (o cárgalo como archivo)
              <textarea
                rows={7}
                value={wtnCsv}
                onChange={(e) => setWtnCsv(e.target.value)}
                placeholder={`${WTN_LAYOUT}\nAna María Torres,FMT-001,ana@correo.mx,WTN-1234,23.4,25.1`}
                disabled={busy}
              />
            </label>
            <div className="form__actions">
              <button className="adm-players__btn adm-players__btn--primary" type="button" onClick={() => void handleImportWtn()} disabled={busy}>
                {busy ? "Importando…" : "Importar WTN"}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="adm-players__panel adm-rank__panel" id="ranking-wtn-status">
        <h2 className="adm-rank__h2">Integración WTN · estado</h2>
        <ul className="ranking-hub-wtn-status">
          <li>✅ Campos WTN en el perfil del jugador (ID + rating singles y dobles) y captura en el alta pública.</li>
          <li>✅ Importación por listado (esta página) como puente operativo.</li>
          <li>⏳ Acceso a la API oficial del ITF World Tennis Number: va vía aval de la federación (FMT). Formato de intercambio: TODS (estándar abierto del ITF). Contacto técnico: ITFTODS@itftennis.com.</li>
        </ul>
      </section>
    </div>
  );
}
